import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { LogIn, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);
    setLoading(true);

    const result = await login(formData);
    
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      if (result.error && result.error.includes('not verified')) {
        setEmailNotVerified(true);
      }
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      await fetch(`${API}/api/auth/resend-verification?email=${encodeURIComponent(formData.email)}`, {
        method: 'POST'
      });
      setError(t('auth.verification_sent'));
      setEmailNotVerified(false);
    } catch (err) {}
    setResending(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mb-4">
              <LogIn className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold" data-testid="login-title">{t('auth.login_title')}</h2>
            <p className="text-gray-600 mt-2">{t('auth.login_subtitle')}</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6" data-testid="login-error">
              {error}
              {emailNotVerified && (
                <button
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="block mt-2 text-orange-500 hover:text-orange-600 font-semibold text-sm"
                  data-testid="resend-verification-btn"
                >
                  {resending ? t('auth.resending') : t('auth.resend_verification')}
                </button>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="email">
                {t('auth.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="vas@email.com"
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="password">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder={t('auth.password_placeholder')}
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              data-testid="login-submit"
            >
              {loading ? t('auth.login_processing') : t('auth.login_action')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {t('auth.no_account')}{' '}
              <Link to="/register" className="text-orange-500 hover:text-orange-600 font-semibold">
                {t('auth.register_action')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;